import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus,
    CheckCircle,
    Search,
    AlertTriangle,
    Clock,
    CheckCircle2,
    MapPin,
    Wrench,
    ArrowRight,
    Snowflake,
    Sparkles,
    RefreshCw,
    History,
    Lightbulb,
    Trash2,
    X
} from 'lucide-react';
import { Badge, StatusTile, ConfirmationModal, SegmentedTabs, segmentedTabId, toast } from '../../components/v2/V2Components';
import TaskService from '../../services/TaskService';
import { getCategoryStyle } from '../../constants/taskCategories';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

const TaskStatus = {
    TODO: 'To Do',
    IN_PROGRESS: 'In Progress',
    COMPLETED: 'Completed',
    BLOCKED: 'Blocked'
};

// The filter strip is an ARIA tablist over a single panel: switching tabs
// re-filters one list rather than swapping distinct regions, so the panel is
// always present and every tab's aria-controls points at a live id.
const TAB_ID_PREFIX = 'maintenance-filter';
const TASK_PANEL_ID = 'maintenance-task-panel';

/**
 * Inline editor for the per-Occurrence completion note. Completion itself
 * stays one-tap — the note is always optional and captured after the fact.
 */
const NoteEditor = ({ initialNote, onSave, onCancel }) => {
    const [draft, setDraft] = useState(initialNote || '');
    return (
        <div className="flex flex-col gap-2 w-full" onClick={e => e.stopPropagation()}>
            <textarea
                autoFocus
                rows={2}
                maxLength={2000}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                placeholder="What was done? (e.g. Replaced with MERV-13 filter)"
                className="w-full rounded-xl p-3 text-sm bg-[var(--bg-elevated)] border border-[var(--border-visible)] text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-bg)]"
            />
            <div className="flex gap-2 justify-end">
                <button
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm font-medium text-[var(--text-secondary)] rounded-lg hover:bg-[var(--bg-hover)]"
                >
                    Cancel
                </button>
                <button
                    onClick={() => onSave(draft.trim())}
                    className="px-3 py-1.5 text-sm font-bold bg-[var(--primary)] text-white rounded-lg hover:opacity-90"
                >
                    Save note
                </button>
            </div>
        </div>
    );
};

/**
 * ADR-0009: the backend serializes frequency as one of three MODES —
 * "One Time" / "Recurring" / "Seasonal". Normalize to lowercase-hyphenated
 * form; retired named cadences (from any stale cached payload) collapse to
 * 'recurring' so filters stay correct during rollout.
 */
const LEGACY_RECURRING = new Set(['daily', 'weekly', 'monthly', 'bi-monthly', 'quarterly', 'annual', 'yearly']);
export const normalizeFrequency = (task) => {
    const freq = (task?.frequency || '').toString().trim().toLowerCase().replace(/\s+/g, '-');
    return LEGACY_RECURRING.has(freq) ? 'recurring' : freq;
};

/**
 * Derived human label (ADR-0009: "always derived, never stored"): computed
 * from the recurrence interval + value, e.g. "Every 3 months"; N=1 renders
 * "Every month".
 */
export const frequencyLabel = (task) => {
    const freq = normalizeFrequency(task);
    if (freq === 'seasonal' || task?.season) return 'Seasonal';
    if (freq === 'one-time') return 'One time';
    if (freq !== 'recurring') return task?.frequency || '';
    const unit = { DAILY: 'day', WEEKLY: 'week', MONTHLY: 'month', YEARLY: 'year' }[
        (task?.recurrence?.interval || '').toString().toUpperCase()
    ];
    if (!unit) return 'Recurring';
    const n = task?.recurrence?.interval_value || 1;
    return n === 1 ? `Every ${unit}` : `Every ${n} ${unit}s`;
};

const Maintenance = () => {
    usePageView(ANALYTICS_EVENTS.MAINTENANCE_VIEWED);
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [isSeriesModalOpen, setIsSeriesModalOpen] = useState(false);
    const [taskToUpdateSeries, setTaskToUpdateSeries] = useState(null);
    // Destructive actions need a confirmation modal (CLAUDE.md pattern).
    const [taskToDelete, setTaskToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const tasksPerPage = 10;

    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('upcoming');
    const [dueDateFilter, setDueDateFilter] = useState('all');
    // Per-Series drill-in: {seriesId, title} filters the History tab to one
    // chain of Occurrences (set by the card's History button).
    const [seriesFilter, setSeriesFilter] = useState(null);
    // Post-complete affordance: offer an optional note for the Occurrence
    // that was just completed, without gating the one-tap completion.
    const [justCompleted, setJustCompleted] = useState(null);
    // Task id whose completion note is being edited inline (History tab).
    const [editingNoteId, setEditingNoteId] = useState(null);
    // Computed template suggestions ("Suggested for your items"). Never
    // stored server-side — accept creates real tasks, dismiss persists a
    // tiny opt-out marker (CONTEXT.md "Template attach").
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionBusy, setSuggestionBusy] = useState(false);

    useEffect(() => { setCurrentPage(1); }, [searchQuery, activeTab, dueDateFilter]);

    useEffect(() => { fetchTasks(); fetchSuggestions(); }, []);

    const fetchTasks = async () => {
        try {
            setLoading(true);
            // Maintenance groups tasks client-side (overdue, today, upcoming),
            // so it needs the full list. `all=true` bypasses server pagination.
            const data = await TaskService.getTasks({ all: true });
            setTasks(data);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch tasks:', err);
            setError('Failed to load tasks');
        } finally {
            setLoading(false);
        }
    };

    const fetchSuggestions = async () => {
        try {
            const data = await TaskService.getSuggestions();
            setSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
        } catch (err) {
            // Suggestions are an enhancement — a failed fetch never blocks
            // the task list itself.
            console.error('Failed to fetch task suggestions:', err);
        }
    };

    const actOnSuggestion = async (suggestion, action) => {
        setSuggestionBusy(true);
        try {
            const selection = [{ item_id: suggestion.item_id, template_name: suggestion.template_name }];
            if (action === 'accept') {
                await TaskService.acceptSuggestions(selection);
                toast.success('Task added');
                await fetchTasks();
            } else {
                await TaskService.dismissSuggestions(selection);
            }
            setSuggestions(prev => prev.filter(
                sug => !(sug.item_id === suggestion.item_id && sug.template_name === suggestion.template_name)
            ));
        } catch (err) {
            console.error('Failed to update suggestion:', err);
            toast.error('Failed to update suggestion');
        } finally {
            setSuggestionBusy(false);
        }
    };

    const acceptAllSuggestions = async () => {
        setSuggestionBusy(true);
        try {
            const selections = suggestions.map(sug => ({ item_id: sug.item_id, template_name: sug.template_name }));
            await TaskService.acceptSuggestions(selections);
            toast.success(`Added ${selections.length} task${selections.length === 1 ? '' : 's'}`);
            setSuggestions([]);
            await fetchTasks();
        } catch (err) {
            console.error('Failed to accept suggestions:', err);
            toast.error('Failed to add suggested tasks');
        } finally {
            setSuggestionBusy(false);
        }
    };

    const toggleStatus = async (id) => {
        const task = tasks.find(t => t.id === id);
        if (!task) return;

        try {
            if (task.status !== TaskStatus.COMPLETED) {
                await TaskService.completeTask(id);
                toast.success('Task completed');
                setJustCompleted({ id, title: task.title });
            } else {
                // Reopen is an undo server-side: the spawned next Occurrence
                // is deleted and the completion stamps clear.
                await TaskService.updateTask(id, { status: TaskStatus.TODO });
            }
            // Completion spawns (and reopen removes) sibling rows — refetch
            // so the list reflects the real chain, not a local guess.
            await fetchTasks();
        } catch (err) {
            console.error('Failed to toggle task status:', err);
            toast.error('Failed to update task status');
        }
    };

    const saveCompletionNote = async (id, note) => {
        try {
            await TaskService.updateTask(id, { completion_note: note || null });
            setTasks(prev => prev.map(t => (t.id === id ? { ...t, completion_note: note || null } : t)));
            setJustCompleted(null);
            setEditingNoteId(null);
            if (note) toast.success('Note saved');
        } catch (err) {
            console.error('Failed to save completion note:', err);
            toast.error('Failed to save note');
        }
    };

    const handleConfirmDelete = async () => {
        // Re-entry guard: a double-click on Confirm must not fire a second
        // DELETE while the first is in flight.
        if (!taskToDelete || isDeleting) return;
        // Capture before the await: closing the modal mid-flight nulls
        // taskToDelete, and reading .id off it afterwards would TypeError.
        const taskId = taskToDelete.id;
        setIsDeleting(true);
        try {
            await TaskService.deleteTask(taskId);
            toast.success('Task deleted');
            setTasks(prev => prev.filter(t => t.id !== taskId));
        } catch (err) {
            console.error('Failed to delete task:', err);
            toast.error('Failed to delete task');
        } finally {
            setIsDeleting(false);
            setTaskToDelete(null);
        }
    };

    const openNewTaskPage = () => navigate('/maintenance/new');
    const openEditTaskPage = (task) => navigate(`/maintenance/edit/${task.id}`);

    const handleConfirmSeriesUpdate = async () => {
        if (!taskToUpdateSeries) return;
        try {
            await TaskService.updateTaskSeries(taskToUpdateSeries);
            toast.success('Task series updated');
            fetchTasks();
        } catch (err) {
            console.error('Failed to update series:', err);
            toast.error('Failed to update series');
        } finally {
            setIsSeriesModalOpen(false);
            setTaskToUpdateSeries(null);
        }
    };

    const getDueStatus = (task) => {
        if (task.status === TaskStatus.COMPLETED) return 'completed';
        if (!task.due_date) return 'none';
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const due = new Date(task.due_date);
        due.setHours(0, 0, 0, 0);
        const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) return 'overdue';
        if (diffDays <= 7) return 'due-soon';
        return 'upcoming';
    };

    const getDueLabel = (task) => {
        if (!task.due_date) return '';
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const due = new Date(task.due_date);
        due.setHours(0, 0, 0, 0);
        const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) return `Due ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} ago`;
        if (diffDays === 0) return 'Due today';
        return `Due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    };

    /* Computed status counts */
    const overdueCount = useMemo(() => tasks.filter(t => getDueStatus(t) === 'overdue').length, [tasks]);
    const dueSoonCount = useMemo(() => tasks.filter(t => getDueStatus(t) === 'due-soon').length, [tasks]);
    const completedCount = useMemo(() => tasks.filter(t => t.status === TaskStatus.COMPLETED).length, [tasks]);

    /* Filter + search */
    const filteredTasks = useMemo(() => tasks.filter(task => {
        const matchesSearch = task.title?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false;

        // Tab-based filter
        const status = getDueStatus(task);
        const freq = normalizeFrequency(task);
        let matchesTab = true;
        if (activeTab === 'upcoming') {
            matchesTab = status !== 'completed';
        } else if (activeTab === 'recurring') {
            // Every recurring cadence (daily through yearly) — drift #2's
            // "yearly tasks appear under no tab" is gone with the modes.
            matchesTab = freq === 'recurring' || (!!task.is_recurring && freq !== 'seasonal' && !task.season);
        } else if (activeTab === 'seasonal') {
            matchesTab = freq === 'seasonal' || !!task.season;
        } else if (activeTab === 'history') {
            // Past Occurrences ARE the Series' history (chain of rows).
            matchesTab = status === 'completed';
        }

        if (seriesFilter && task.series_id !== seriesFilter.seriesId) {
            return false;
        }

        // Due date sub-filter. History shows past completions — due-date
        // predicates (which exclude completed tasks) don't apply there,
        // or an "Overdue" pre-selection would blank the whole tab.
        let matchesDueDate = true;
        if (dueDateFilter !== 'all' && activeTab !== 'history') {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const dueDate = task.due_date ? new Date(task.due_date) : null;

            if (dueDateFilter === 'overdue') {
                matchesDueDate = dueDate && dueDate < today && task.status !== TaskStatus.COMPLETED;
            } else if (dueDateFilter === 'today') {
                matchesDueDate = dueDate && dueDate.toDateString() === today.toDateString();
            } else if (dueDateFilter === 'week') {
                const weekFromNow = new Date(today);
                weekFromNow.setDate(today.getDate() + 7);
                matchesDueDate = dueDate && dueDate >= today && dueDate <= weekFromNow;
            } else if (dueDateFilter === 'month') {
                const monthFromNow = new Date(today);
                monthFromNow.setMonth(today.getMonth() + 1);
                matchesDueDate = dueDate && dueDate >= today && dueDate <= monthFromNow;
            }
        }

        return matchesSearch && matchesTab && matchesDueDate;
    }), [tasks, searchQuery, activeTab, dueDateFilter, seriesFilter]);

    // Sort: History shows newest completion first; other tabs show overdue
    // first, then by due date ascending.
    const sortedTasks = useMemo(() => [...filteredTasks].sort((a, b) => {
        if (activeTab === 'history') {
            const doneA = a.last_completed ? new Date(a.last_completed) : new Date(0);
            const doneB = b.last_completed ? new Date(b.last_completed) : new Date(0);
            return doneB - doneA;
        }
        const aStatus = getDueStatus(a);
        const bStatus = getDueStatus(b);
        if (aStatus === 'overdue' && bStatus !== 'overdue') return -1;
        if (bStatus === 'overdue' && aStatus !== 'overdue') return 1;
        const dateA = a.due_date ? new Date(a.due_date) : new Date('9999-12-31');
        const dateB = b.due_date ? new Date(b.due_date) : new Date('9999-12-31');
        return dateA - dateB;
    }), [filteredTasks, activeTab]);

    const totalPages = Math.ceil(sortedTasks.length / tasksPerPage);
    const startIndex = (currentPage - 1) * tasksPerPage;
    const paginatedTasks = sortedTasks.slice(startIndex, startIndex + tasksPerPage);

    const tabs = [
        { key: 'upcoming', label: 'Upcoming' },
        { key: 'recurring', label: 'Recurring' },
        { key: 'seasonal', label: 'Seasonal' },
        { key: 'history', label: 'History' }
    ];

    const viewSeriesHistory = (task) => {
        setSeriesFilter({ seriesId: task.series_id, title: task.title });
        setActiveTab('history');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-10 animate-fade-in">
            <ConfirmationModal
                isOpen={!!taskToDelete}
                onClose={() => setTaskToDelete(null)}
                onConfirm={handleConfirmDelete}
                title="Delete Task"
                message={taskToDelete ? `Delete "${taskToDelete.title}"? This cannot be undone.` : ''}
                confirmText={isDeleting ? 'Deleting…' : 'Delete'}
                isDanger
                isLoading={isDeleting}
            />
            <ConfirmationModal
                isOpen={isSeriesModalOpen}
                onClose={() => { setIsSeriesModalOpen(false); setTaskToUpdateSeries(null); }}
                onConfirm={handleConfirmSeriesUpdate}
                title="Update Task Series"
                message="Update all future tasks in this recurring series? This will regenerate upcoming scheduled dates."
                confirmText="Update Series"
            />

            {/* Editorial Header */}
            <header className="flex flex-col md:flex-row justify-between items-end gap-6">
                <div className="max-w-xl">
                    <h1 className="font-headline text-4xl md:text-5xl font-extrabold tracking-tight text-[var(--primary)] mb-3">
                        Maintenance Scheduler
                    </h1>
                    <p className="text-[var(--text-secondary)] text-base leading-relaxed">
                        Keep your House in peak condition with curated care routines designed for the current climate.
                    </p>
                </div>
                <button
                    onClick={openNewTaskPage}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[var(--primary)] to-[var(--primary-container)] text-white rounded-full font-bold shadow-lg hover:opacity-90 transition-all active:scale-95 whitespace-nowrap"
                >
                    <Plus size={18} />
                    <span>New Task</span>
                </button>
            </header>

            {error && (
                <div className="p-4 bg-[var(--error-container)] text-[var(--error)] rounded-xl">
                    {error}
                    <button onClick={fetchTasks} className="ml-4 underline font-medium">Retry</button>
                </div>
            )}

            {/* Status Overview Bento Grid */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatusTile
                    label="Overdue"
                    value={String(overdueCount).padStart(2, '0')}
                    icon={AlertTriangle}
                    variant="overdue"
                />
                <StatusTile
                    label="Due Soon"
                    value={String(dueSoonCount).padStart(2, '0')}
                    icon={Clock}
                    variant="due-soon"
                />
                <StatusTile
                    label="Completed"
                    value={String(completedCount).padStart(2, '0')}
                    icon={CheckCircle2}
                    variant="completed"
                />
            </section>

            {/* Pill Tab Toggle + Search — stacks until `lg` because at 768-1023px the
                strip, the search box and the date select together need ~843px. */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <SegmentedTabs
                    tabs={tabs}
                    activeTab={activeTab}
                    onSelect={(key) => { setActiveTab(key); setSeriesFilter(null); }}
                    semantics="tabs"
                    ariaLabel="Task filters"
                    idPrefix={TAB_ID_PREFIX}
                    panelId={TASK_PANEL_ID}
                    className="lg:w-auto lg:min-w-[420px]"
                />

                <div className="flex gap-3 items-center">
                    <div className="bg-[var(--bg-card)] rounded-xl flex items-center px-4 py-2.5 gap-2 shadow-[var(--shadow-sm)]">
                        <Search size={16} className="text-[var(--text-muted)]" />
                        <input
                            placeholder="Search tasks..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="text-sm outline-none w-32 md:w-48 bg-transparent text-[var(--text-main)] placeholder-[var(--text-muted)] font-body"
                        />
                    </div>
                    <select
                        value={dueDateFilter}
                        onChange={(e) => setDueDateFilter(e.target.value)}
                        className="px-4 py-2.5 rounded-xl bg-[var(--bg-card)] text-[var(--text-main)] text-sm focus:ring-2 focus:ring-[var(--primary)] focus:outline-none shadow-[var(--shadow-sm)]"
                    >
                        <option value="all">All Dates</option>
                        <option value="overdue">Overdue</option>
                        <option value="today">Due Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                    </select>
                </div>
            </div>

            {/* Series drill-in chip */}
            {seriesFilter && (
                <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--primary-bg)] text-[var(--primary)] rounded-full text-sm font-bold">
                        <History size={14} />
                        Series: {seriesFilter.title}
                        <button
                            onClick={() => setSeriesFilter(null)}
                            aria-label="Clear series filter"
                            className="hover:opacity-70"
                        >
                            <X size={14} />
                        </button>
                    </span>
                </div>
            )}

            {/* Post-complete note affordance — completion stayed one-tap;
                the note is optional and dismissible. */}
            {justCompleted && (
                <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-5 space-y-3" data-testid="completion-note-banner">
                    <div className="flex items-center justify-between gap-3">
                        <p className="text-sm text-[var(--text-main)]">
                            <CheckCircle2 size={16} className="inline mr-2 text-[var(--primary)]" />
                            Completed <span className="font-bold">{justCompleted.title}</span> — add a note for the record?
                        </p>
                        <button
                            onClick={() => setJustCompleted(null)}
                            aria-label="Dismiss note prompt"
                            className="text-[var(--text-muted)] hover:text-[var(--text-main)]"
                        >
                            <X size={16} />
                        </button>
                    </div>
                    <NoteEditor
                        initialNote=""
                        onSave={note => (note ? saveCompletionNote(justCompleted.id, note) : setJustCompleted(null))}
                        onCancel={() => setJustCompleted(null)}
                    />
                </div>
            )}

            {/* Information-Rich Task List — the tablist's single panel. tabIndex={0}
                because task cards are clickable divs, not focusable elements, and a
                tabpanel with no focusable children must be reachable itself. */}
            <div
                role="tabpanel"
                id={TASK_PANEL_ID}
                aria-labelledby={segmentedTabId(TAB_ID_PREFIX, activeTab)}
                tabIndex={0}
                className="flex flex-col gap-5"
            >
                {paginatedTasks.length > 0 ? (
                    paginatedTasks.map(task => {
                        const dueStatus = getDueStatus(task);
                        const isOverdue = dueStatus === 'overdue';
                        const isCompleted = task.status === TaskStatus.COMPLETED;
                        const catStyle = getCategoryStyle(task);
                        const CatIcon = catStyle.icon;

                        return (
                            <div
                                key={task.id}
                                data-testid="task-card"
                                className={`group relative overflow-hidden bg-[var(--bg-card)] p-6 lg:p-7 rounded-2xl flex flex-wrap lg:flex-nowrap items-center gap-6 transition-all hover:shadow-[0_8px_24px_rgba(0,0,0,0.04)] cursor-pointer ${isOverdue ? 'border-l-[6px] border-[var(--error)]' : ''
                                    } ${isCompleted ? 'opacity-60' : ''}`}
                                onClick={() => openEditTaskPage(task)}
                            >
                                {/* Category Icon */}
                                <div className={`flex-shrink-0 w-14 h-14 rounded-xl ${catStyle.bgClass} ${catStyle.textClass} flex items-center justify-center`}>
                                    <CatIcon size={28} />
                                </div>

                                {/* Title + Priority + Location */}
                                <div className="flex-grow space-y-1 min-w-[250px]">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <h3 className={`text-lg font-bold text-[var(--primary)] ${isCompleted ? 'line-through' : ''}`}>
                                            {task.title}
                                        </h3>
                                        {task.priority === 'high' && <Badge status="High Priority" />}
                                        {task.priority === 'medium' && <Badge status="Scheduled" />}
                                        {task.priority === 'low' && <Badge status="Routine" />}
                                        {!task.priority && task.frequency && normalizeFrequency(task) !== 'one-time' && (
                                            <Badge status="Routine" />
                                        )}
                                    </div>
                                    <p className="text-[var(--text-secondary)] text-sm flex items-center gap-1">
                                        <MapPin size={12} />
                                        {task.room_name || 'General'} • {isCompleted
                                            ? task.last_completed
                                                ? `Completed ${new Date(task.last_completed).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
                                                : 'Completed'
                                            : getDueLabel(task)}
                                    </p>
                                    {isCompleted && (
                                        editingNoteId === task.id ? (
                                            <NoteEditor
                                                initialNote={task.completion_note}
                                                onSave={note => saveCompletionNote(task.id, note)}
                                                onCancel={() => setEditingNoteId(null)}
                                            />
                                        ) : task.completion_note ? (
                                            <p className="text-sm italic text-[var(--text-secondary)]">
                                                “{task.completion_note}”
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setEditingNoteId(task.id); }}
                                                    className="ml-2 not-italic text-xs font-bold text-[var(--primary)] hover:underline"
                                                >
                                                    Edit note
                                                </button>
                                            </p>
                                        ) : (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setEditingNoteId(task.id); }}
                                                className="text-xs font-bold text-[var(--primary)] hover:underline"
                                            >
                                                + Add note
                                            </button>
                                        )
                                    )}
                                </div>

                                {/* Metadata Columns (xl only) */}
                                <div className="hidden xl:flex items-center gap-8 px-6 flex-shrink-0">
                                    {task.frequency && normalizeFrequency(task) !== 'one-time' && (
                                        <div className="text-center">
                                            <p className="text-[11px] text-[var(--text-secondary)] font-bold uppercase tracking-widest">Frequency</p>
                                            <p className="text-sm font-semibold text-[var(--secondary)]">{frequencyLabel(task)}</p>
                                        </div>
                                    )}
                                    {task.estimated_duration && (
                                        <div className="text-center">
                                            <p className="text-[11px] text-[var(--text-secondary)] font-bold uppercase tracking-widest">Est. Duration</p>
                                            <p className="text-sm font-semibold text-[var(--secondary)]">{task.estimated_duration}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center gap-3 ml-auto">
                                    {task.series_id && !seriesFilter && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                viewSeriesHistory(task);
                                            }}
                                            className="px-4 py-2 bg-[var(--bg-elevated)] text-[var(--primary)] rounded-xl font-bold hover:bg-[var(--bg-hover)] transition-colors text-sm hidden md:flex items-center gap-2"
                                            aria-label="View series history"
                                        >
                                            <History size={14} />
                                            History
                                        </button>
                                    )}
                                    {(task.is_recurring || (task.frequency && normalizeFrequency(task) !== 'one-time')) && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setTaskToUpdateSeries(task.id);
                                                setIsSeriesModalOpen(true);
                                            }}
                                            className="px-4 py-2 bg-[var(--secondary-container)] text-[var(--secondary-container-text)] rounded-xl font-bold hover:opacity-90 transition-colors text-sm hidden md:flex items-center gap-2"
                                            aria-label="Update recurring task series"
                                        >
                                            <RefreshCw size={14} />
                                            Update Series
                                        </button>
                                    )}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openEditTaskPage(task);
                                        }}
                                        className="px-4 py-2 bg-[var(--bg-elevated)] text-[var(--primary)] rounded-xl font-bold hover:bg-[var(--bg-hover)] transition-colors text-sm hidden md:block"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setTaskToDelete(task);
                                        }}
                                        className="p-2 text-[var(--text-muted)] rounded-xl hover:text-[var(--error)] hover:bg-[var(--bg-hover)] transition-colors"
                                        aria-label={`Delete ${task.title}`}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleStatus(task.id);
                                        }}
                                        className={`px-4 py-2 rounded-xl font-bold flex items-center gap-2 transition-all text-sm ${isCompleted
                                            ? 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]'
                                            : 'bg-[var(--primary)] text-white hover:opacity-90'
                                        }`}
                                        aria-label={isCompleted ? 'Reopen task' : 'Mark task as done'}
                                    >
                                        <CheckCircle size={16} />
                                        <span className="hidden md:inline" aria-hidden="true">{isCompleted ? 'Reopen' : 'Mark Done'}</span>
                                    </button>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="text-center py-16">
                        <Wrench size={48} className="mx-auto text-[var(--text-muted)] mb-4" />
                        <h3 className="font-headline text-xl font-bold text-[var(--text-main)] mb-2">
                            {searchQuery ? 'No tasks found' : 'No maintenance tasks yet'}
                        </h3>
                        <p className="text-[var(--text-secondary)] mb-6 max-w-md mx-auto">
                            {searchQuery
                                ? 'Try adjusting your search or filter criteria'
                                : 'Start building your maintenance routine by adding your first task'}
                        </p>
                        {!searchQuery && (
                            <button
                                onClick={openNewTaskPage}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--primary)] text-white rounded-xl font-semibold hover:opacity-90 transition"
                            >
                                <Plus size={18} />
                                <span>Create Your First Task</span>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between pt-2">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-5 py-2.5 text-sm font-medium text-[var(--text-main)] bg-[var(--bg-hover)] rounded-xl hover:bg-[var(--bg-elevated)] disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-[var(--text-secondary)]">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="px-5 py-2.5 text-sm font-medium text-[var(--text-main)] bg-[var(--bg-hover)] rounded-xl hover:bg-[var(--bg-elevated)] disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Suggested-for-your-items review section (computed live;
                inline Card per the no-overlay rule). Rendered AFTER the task
                list so the user's real tasks lead the page and suggestions
                read as a follow-on prompt. It sits outside the tabpanel
                because suggestions are tab-independent. */}
            {suggestions.length > 0 && (
                <section
                    data-testid="suggestions-section"
                    className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-6 space-y-4"
                >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-[var(--primary-bg)] flex items-center justify-center">
                                <Lightbulb size={20} className="text-[var(--primary)]" />
                            </div>
                            <div>
                                <h3 className="font-headline text-lg font-extrabold text-[var(--primary)]">
                                    Suggested for your items
                                </h3>
                                <p className="text-sm text-[var(--text-secondary)]">
                                    Recommended maintenance based on what you own — add the ones you want.
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={acceptAllSuggestions}
                            disabled={suggestionBusy}
                            className="px-4 py-2 bg-[var(--primary)] text-white rounded-xl font-bold text-sm hover:opacity-90 disabled:opacity-50"
                        >
                            Add all ({suggestions.length})
                        </button>
                    </div>
                    <ul className="divide-y divide-[var(--border)]">
                        {suggestions.map(sug => (
                            <li
                                key={`${sug.item_id}-${sug.template_name}`}
                                className="py-3 flex items-center gap-4 flex-wrap"
                            >
                                <div className="flex-grow min-w-[220px]">
                                    <p className="font-bold text-[var(--text-main)] text-sm">{sug.title}</p>
                                    <p className="text-xs text-[var(--text-secondary)]">
                                        {sug.item_name}
                                        {sug.is_recurring && sug.interval && (
                                            <> • Every {sug.interval_value > 1 ? `${sug.interval_value} ` : ''}
                                            {sug.interval === 'YEARLY' ? 'year' : 'month'}{sug.interval_value > 1 ? 's' : ''}</>
                                        )}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 ml-auto">
                                    <button
                                        onClick={() => actOnSuggestion(sug, 'dismiss')}
                                        disabled={suggestionBusy}
                                        className="px-3 py-1.5 text-sm font-medium text-[var(--text-secondary)] rounded-lg hover:bg-[var(--bg-hover)] disabled:opacity-50"
                                        aria-label={`Dismiss ${sug.title}`}
                                    >
                                        No thanks
                                    </button>
                                    <button
                                        onClick={() => actOnSuggestion(sug, 'accept')}
                                        disabled={suggestionBusy}
                                        className="px-3 py-1.5 text-sm font-bold bg-[var(--primary-bg)] text-[var(--primary)] rounded-lg hover:opacity-80 disabled:opacity-50"
                                        aria-label={`Add ${sug.title}`}
                                    >
                                        Add task
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Bottom Editorial Section */}
            {tasks.length > 0 && (
                <section className="grid grid-cols-1 xl:grid-cols-2 gap-8 mt-6">
                    {/* Pro Tip Card */}
                    <div className="p-8 bg-[var(--bg-hover)] rounded-3xl flex gap-6 items-start">
                        <div className="w-16 h-16 rounded-xl bg-[var(--primary-bg)] flex items-center justify-center flex-shrink-0">
                            <Sparkles size={28} className="text-[var(--primary)]" />
                        </div>
                        <div>
                            <h4 className="font-headline text-lg font-extrabold text-[var(--primary)] mb-2">Maintenance Pro Tip</h4>
                            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                                During high-pollen seasons, your HVAC system works 30% harder. Changing filters every 45 days instead of 90 can extend the lifespan of your unit by up to 5 years.
                            </p>
                        </div>
                    </div>

                    {/* Seasonal Sweep Card (dark) */}
                    <div className="p-8 bg-[var(--primary-container)] rounded-3xl relative overflow-hidden">
                        <div className="relative z-10 flex flex-col justify-between h-full">
                            <div>
                                <h4 className="font-headline text-lg font-extrabold text-white mb-1">Upcoming Seasonal Sweep</h4>
                                <p className="text-sm text-[var(--primary-container-text)] leading-relaxed">
                                    Seasonal preparation tasks have been identified for your home. Review and add them to your maintenance schedule.
                                </p>
                            </div>
                            <button className="mt-4 text-sm font-bold text-white flex items-center gap-1 hover:underline">
                                Review seasonal plan
                                <ArrowRight size={14} />
                            </button>
                        </div>
                        <div className="absolute -right-4 top-1/2 -translate-y-1/2 opacity-5 pointer-events-none">
                            <Snowflake size={140} />
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

export default Maintenance;
